"use client";

/**
 * Shared watchlist state for the heart toggles.
 *
 * One /api/watchlist/ids fetch per session feeds every heart on every
 * card via a module-level store (no context provider needed - cards
 * mount in grids, drawers and map popups across unrelated trees).
 * Toggles are optimistic: flip locally, fire the request, roll back on
 * failure.
 *
 * Two owners (2026-09-20): a signed-in user, or an anonymous DEVICE.
 * Without a session the browser's push subscription is the identity
 * (same authority as the residence follow): the first heart asks for
 * notification permission, a few hearts are held per device, and the
 * next one opens the sign-in sheet (the conversion ladder). Signing in
 * claims the device's hearts into the account.
 */

import { useCallback, useEffect, useState } from "react";
import { API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { useUndo } from "@/lib/undo";
import { useT } from "@/lib/locale-context";
import { getCurrentPushEndpoint, getOrCreatePushSubscription } from "@/lib/push";
import { openPinLimit } from "@/lib/use-pin-limit";

type Listener = () => void;

const store = {
  ids: null as Set<string> | null,
  fetched: false,
  // undefined = not looked up yet; null = this browser has no push
  // subscription (never prompted, or refused).
  endpoint: undefined as string | null | undefined,
  claimedFor: null as string | null,
  anonLimit: 5,
  listeners: new Set<Listener>(),
  notify() {
    this.listeners.forEach((l) => l());
  },
};

async function resolveEndpoint(): Promise<string | null> {
  if (store.endpoint !== undefined) return store.endpoint;
  try {
    store.endpoint = await getCurrentPushEndpoint();
  } catch {
    store.endpoint = null;
  }
  return store.endpoint;
}

/**
 * A surface whose saves belong to someone the site's session does not
 * know: the Telegram Mini App, where the owner is the Telegram user (or
 * the account they linked), proven by Telegram on every request. It plugs
 * its own endpoints in here and every heart on every card then reads and
 * writes through them, with the same optimistic flip and undo.
 */
export interface WatchBackend {
  fetchIds(): Promise<string[] | null>;
  /** Throws when the save did not happen (the flip rolls back). */
  add(id: string): Promise<void>;
  remove(id: string): Promise<void>;
}

let backend: WatchBackend | null = null;

export function setWatchBackend(next: WatchBackend | null): void {
  backend = next;
  store.ids = null;
  store.fetched = false;
  store.claimedFor = null;
  store.notify();
  if (next) void fetchIds(false);
}

async function fetchIds(signedIn: boolean): Promise<void> {
  if (store.fetched) return;
  store.fetched = true;
  if (backend) {
    const ids = await backend.fetchIds().catch(() => null);
    store.ids = new Set(ids ?? []);
    store.notify();
    return;
  }
  try {
    let url = `${API_BASE}/watchlist/ids`;
    if (!signedIn) {
      const ep = await resolveEndpoint();
      if (!ep) {
        store.ids = new Set();
        store.notify();
        return;
      }
      url += `?endpoint=${encodeURIComponent(ep)}`;
    }
    const r = await fetch(url, { credentials: "include" });
    if (!r.ok) return;
    const d = await r.json();
    store.ids = new Set<string>(d.ids ?? []);
    if (typeof d.limit === "number") store.anonLimit = d.limit;
    store.notify();
  } catch {
    /* hearts just stay off */
  }
}

/** A fresh session claims whatever this device hearted anonymously,
 *  then reloads the id set as the account's. Once per user per page. */
async function claimDevice(userId: string): Promise<void> {
  if (store.claimedFor === userId) return;
  store.claimedFor = userId;
  const ep = await resolveEndpoint();
  if (ep) {
    try {
      await fetch(`${API_BASE}/watchlist/claim`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ endpoint: ep }),
      });
    } catch {
      /* the hearts stay device-bound until the next visit */
    }
  }
  store.fetched = false;
}

/** Sync the heart id-set after a save/remove that happened OUTSIDE the
 *  toggle() path (e.g. the My-Map editor in the detail panel or the pins
 *  store), so hearts on cards reflect it without a refetch. */
export function setWatchedLocal(id: string, watched: boolean): void {
  if (!store.ids) store.ids = new Set();
  if (watched) store.ids.add(id);
  else store.ids.delete(id);
  store.notify();
}

function rollback(id: string, watched: boolean) {
  if (!store.ids) store.ids = new Set();
  if (watched) store.ids.add(id);
  else store.ids.delete(id);
  store.notify();
}

async function toggleSigned(id: string, watched: boolean): Promise<void> {
  const r = await fetch(
    watched ? `${API_BASE}/watchlist/${encodeURIComponent(id)}` : `${API_BASE}/watchlist`,
    watched
      ? { method: "DELETE", credentials: "include" }
      : {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ listing_id: id }),
        },
  );
  if (!r.ok) {
    // Hit the free pin cap on save -> show the global Pro upsell (then
    // fall through to the rollback so the bookmark un-fills).
    if (!watched && r.status === 400) {
      const detail = (await r.json().catch(() => ({})))?.detail;
      if (detail && typeof detail === "object" && detail.reason === "limit_reached") {
        openPinLimit(typeof detail.limit === "number" ? detail.limit : 25);
      }
    }
    throw new Error(String(r.status));
  }
  // Keep the My-Map pins store (drawer editor, personal map) in
  // step: the heart just created/deleted the same watchlist row.
  // Dynamic import - use-my-pins statically imports this module.
  void import("@/lib/use-my-pins").then((m) => m.syncPinsFromWatch(id, !watched));
}

async function toggleDevice(id: string, watched: boolean): Promise<void> {
  if (watched) {
    const ep = await resolveEndpoint();
    if (!ep) return;
    const r = await fetch(
      `${API_BASE}/watchlist/${encodeURIComponent(id)}?endpoint=${encodeURIComponent(ep)}`,
      { method: "DELETE", credentials: "include" },
    );
    if (!r.ok) throw new Error(String(r.status));
    return;
  }
  // First heart on this device asks for notification permission: the
  // subscription is what makes the heart worth anything (price pings)
  // and what identifies the device afterwards. Refused or unsupported
  // (iOS Safari outside the home screen) -> the sign-in sheet.
  const { sub } = await getOrCreatePushSubscription("/follows/config");
  if (!sub) {
    openPinLimit(store.anonLimit, "account");
    throw new Error("no_push");
  }
  store.endpoint = sub.endpoint;
  const r = await fetch(`${API_BASE}/watchlist/anon`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ listing_id: id, subscription: sub.toJSON() }),
  });
  if (!r.ok) {
    if (r.status === 403) {
      const detail = (await r.json().catch(() => ({})))?.detail;
      const limit = detail && typeof detail === "object" && typeof detail.limit === "number"
        ? detail.limit : store.anonLimit;
      openPinLimit(limit, "account");
    }
    throw new Error(String(r.status));
  }
}

export function useWatchlist() {
  const undo = useUndo();
  const t = useT();
  const { user } = useAuth();
  const [, bump] = useState(0);

  useEffect(() => {
    const l = () => bump((v) => v + 1);
    store.listeners.add(l);
    if (backend) {
      void fetchIds(false);
    } else if (user) {
      void claimDevice(user.id).then(() => fetchIds(true));
    } else {
      void fetchIds(false);
    }
    return () => { store.listeners.delete(l); };
  }, [user]);

  // The watched-id set has loaded (the /watchlist/ids fetch resolved).
  // Lets the watchlist page wait before filtering its cards by `has`, so
  // a slow id-fetch doesn't briefly hide every card.
  const ready = store.ids !== null;

  const has = useCallback(
    (id: string | null | undefined) => !!(id && store.ids?.has(id)),
    [],
  );

  const signedIn = !!user;
  const toggle = useCallback(async (id: string) => {
    if (!store.ids) store.ids = new Set();
    const watched = store.ids.has(id);
    // Optimistic flip.
    if (watched) store.ids.delete(id);
    else store.ids.add(id);
    store.notify();
    const run = async () => {
      try {
        if (backend) await (watched ? backend.remove(id) : backend.add(id));
        else if (signedIn) await toggleSigned(id, watched);
        else await toggleDevice(id, watched);
      } catch {
        rollback(id, watched);
      }
    };
    if (!watched) { await run(); return; }
    // Unsaving is undoable: the heart goes off at once, the server waits
    // for the toast (owner, 2026-10-05).
    undo.schedule({
      key: `pin:${id}`,
      message: t("undo.pin_removed"),
      commit: run,
      undo: () => { if (!store.ids) store.ids = new Set(); store.ids.add(id); store.notify(); },
    });
  }, [signedIn, undo, t]);

  return { signedIn, ready, has, toggle };
}
